import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Lock, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-8">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{language === 'hi' ? 'वापस जाएं' : 'Go Back'}</span>
          </button>
          <h1 className={`text-3xl md:text-4xl font-black ${language === 'hi' ? 'font-hindi' : ''}`}>
            {language === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-orange-100">
          
          {/* Introduction */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
              <h2 className={`text-2xl font-bold text-gray-900 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? 'आपकी गोपनीयता हमारी प्राथमिकता' : 'Your Privacy Is Our Priority'}
              </h2>
            </div>
            <p className={`text-gray-600 leading-relaxed ${language === 'hi' ? 'font-hindi' : ''}`}>
              {language === 'hi'
                ? 'Karma Kundali आपकी व्यक्तिगत जानकारी की सुरक्षा के लिए प्रतिबद्ध है। यह गोपनीयता नीति बताती है कि हम आपकी जानकारी कैसे एकत्र, उपयोग और सुरक्षित करते हैं।'
                : 'Karma Kundali is committed to protecting your personal information. This privacy policy explains how we collect, use and protect your information.'}
            </p>
          </div>

          {/* Policy Points */}
          <div className="space-y-8">
            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '1. जानकारी जो हम एकत्र करते हैं' : '1. Information We Collect'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'व्यक्तिगत जानकारी: नाम, ईमेल, फोन नंबर' : 'Personal Information: Name, email, phone number'}</li>
                <li>{language === 'hi' ? 'जन्म विवरण: जन्म तिथि, समय, स्थान' : 'Birth Details: Date of birth, time, place'}</li>
                <li>{language === 'hi' ? 'भुगतान जानकारी: Razorpay द्वारा सुरक्षित रूप से प्रोसेस' : 'Payment Information: Processed securely by Razorpay'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '2. जानकारी का उपयोग' : '2. How We Use Information'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'आपकी कुंडली रिपोर्ट तैयार करने के लिए' : 'To prepare your Kundli report'}</li>
                <li>{language === 'hi' ? 'रिपोर्ट ईमेल पर भेजने के लिए' : 'To send report to your email'}</li>
                <li>{language === 'hi' ? 'ऑर्डर अपडेट और सपोर्ट के लिए संपर्क' : 'To contact for order updates and support'}</li>
                <li>{language === 'hi' ? 'सेवा सुधार के लिए' : 'For service improvement'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '3. डेटा सुरक्षा' : '3. Data Security'}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <p className={`text-gray-700 ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {language === 'hi'
                    ? 'हम आपकी जानकारी की सुरक्षा के लिए उद्योग-मानक सुरक्षा उपाय का उपयोग करते हैं। आपका डेटा एन्क्रिप्टेड सर्वर पर सुरक्षित रूप से संग्रहीत है। भुगतान Razorpay के PCI DSS अनुपालन प्लेटफॉर्म के माध्यम से सुरक्षित रूप से प्रोसेस होता है।'
                    : 'We use industry-standard security measures to protect your information. Your data is securely stored on encrypted servers. Payments are processed securely through Razorpay\'s PCI DSS compliant platform.'}
                </p>
              </div>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '4. जानकारी साझाकरण' : '4. Information Sharing'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'हम आपकी जानकारी किसी तीसरे पक्ष को नहीं बेचते।' : 'We do not sell your information to third parties.'}</li>
                <li>{language === 'hi' ? 'जानकारी केवल सेवा प्रदान करने के लिए उपयोग होती है।' : 'Information is only used to provide our services.'}</li>
                <li>{language === 'hi' ? 'कानूनी आवश्यकता पर ही जानकारी साझा की जा सकती है।' : 'Information may be shared only when legally required.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '5. कुकीज़ और ट्रैकिंग' : '5. Cookies & Tracking'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'हम बेहतर अनुभव के लिए कुकीज़ का उपयोग करते हैं।' : 'We use cookies for better user experience.'}</li>
                <li>{language === 'hi' ? 'Meta Pixel विज्ञापन प्रभावशीलता मापने के लिए उपयोग होता है।' : 'Meta Pixel is used to measure advertising effectiveness.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '6. आपके अधिकार' : '6. Your Rights'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'आप अपनी जानकारी देखने का अनुरोध कर सकते हैं।' : 'You can request to view your information.'}</li>
                <li>{language === 'hi' ? 'आप गलत जानकारी को सुधारने के लिए कह सकते हैं।' : 'You can ask to correct inaccurate information.'}</li>
                <li>{language === 'hi' ? 'आप डेटा हटाने का अनुरोध कर सकते हैं (कानूनी सीमाओं के अधीन)।' : 'You can request data deletion (subject to legal limitations).'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '7. संपर्क' : '7. Contact'}
              </h3>
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <p className={`text-gray-700 mb-4 ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {language === 'hi' ? 'गोपनीयता संबंधी प्रश्नों के लिए:' : 'For privacy related questions:'}
                </p>
                <div className="space-y-2">
                  <a href="mailto:karmakundali2@gmail.com" className="flex items-center gap-2 text-orange-600 hover:text-orange-700">
                    <Mail className="w-5 h-5" />
                    <span>karmakundali2@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              {language === 'hi' ? 'अंतिम अपडेट: फरवरी 2026' : 'Last Updated: February 2026'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
